import {
    Box,
    Divider,
    IconButton,
    List,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ReactComponent as FilterIcon } from "../../../assets/svg/noti-filter.svg";
import { NotificationItem, NotificationType } from "../types";
import { DataDownloadAlert } from "./dataDownloadAlert";
import { SensorAlert } from "./sensorAlert";
import { EventAlert } from "./eventAlert";
import { APP_CONFIG } from "config/appConfig";
import { getEventsByOrganizationIdCached } from "service/queryCache/eventOrganizationCache";
import { isHttpSuccess } from "utils/functions";

// Styled components
const StyledTabs = styled(Tabs)(
    () => `
    & .MuiTabs-root {
        background-color: #2f3a51;
    }
    & .MuiTab-root {
        color: #ffffff;
        text-transform: none;
        font-size: 16px;
        font-weight: 400;
        &.Mui-selected {
            color: #00CBAF;
        }
    }
    & .MuiTabs-indicator {
        background-color: #00CBAF;
    }
`
);

const TabCountBadge = styled("span")<{ isSelected: boolean }>(
    ({ isSelected }) => `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    height: 30px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 500;
    margin-left: 8px;
    background-color: ${isSelected ? "#00CBAF" : "#2F3A51"};
    color: ${isSelected ? "#ffffff" : "#828fab"};
    border: ${isSelected ? "none" : "1px solid #47536d"};
`
);

// Notification Popup Header Component
interface NotificationPopupHeaderProps {
    onFilterClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    hasActiveFilters?: boolean;
}

export const NotificationPopupHeader = React.memo(
    ({ onFilterClick, hasActiveFilters }: NotificationPopupHeaderProps) => (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 20px",
            }}
        >
            <Typography
                variant="subtitle1"
                sx={{ fontSize: "18px", fontWeight: 500, color: "#ffffff" }}
            >
                Notifications
            </Typography>
            <Box sx={{ position: "relative" }}>
                <IconButton
                    size="small"
                    sx={{ color: "#828fab" }}
                    onClick={onFilterClick}
                >
                    <FilterIcon width={24} height={24} />
                </IconButton>
                {hasActiveFilters && (
                    <Box
                        aria-label="filter-indicator"
                        sx={{
                            position: "absolute",
                            top: "6px",
                            right: "6px",
                            width: "8px",
                            height: "8px",
                            backgroundColor: "#00CBAF",
                            borderRadius: "50%",
                        }}
                    />
                )}
            </Box>
        </Box>
    )
);

// Notification Tabs Component
interface NotificationTabsProps {
    selectedTab: NotificationType;
    onTabChange: (
        event: React.SyntheticEvent,
        newValue: NotificationType
    ) => void;
    tabCounts: {
        all: number;
        sensor: number;
        download: number;
        event: number;
    };
}

export const NotificationTabs = ({
    selectedTab,
    onTabChange,
    tabCounts,
}: NotificationTabsProps) => {
    const renderTabWithBadge = (
        label: string,
        value: NotificationType,
        count: number
    ) => (
        <Tab
            disableRipple={true}
            label={
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    {label}
                    <TabCountBadge isSelected={selectedTab === value}>
                        {count}
                    </TabCountBadge>
                </Box>
            }
            value={value}
        />
    );

    return (
        <Box
            sx={{
                backgroundColor: "#2F3A51",
                borderBottom: "1px solid #47536d",
            }}
        >
            <StyledTabs value={selectedTab} onChange={onTabChange}>
                {renderTabWithBadge("All", "all", tabCounts.all)}
                {renderTabWithBadge(
                    "Sensor Alerts",
                    "sensor",
                    tabCounts["sensor"]
                )}

                {/* Event Alerts is only for cloud */}
                {!APP_CONFIG.APP_IS_ONPREM &&
                    renderTabWithBadge(
                        "Event Alerts",
                        "event",
                        tabCounts["event"]
                    )}


                {/* Data Download is only for on-prem */}
                {APP_CONFIG.APP_IS_ONPREM &&
                    renderTabWithBadge(
                        "Data Download",
                        "download",
                        tabCounts.download
                    )}
            </StyledTabs>
        </Box>
    );
};

// Notification List Component
interface NotificationListProps {
    notifications: NotificationItem[];
    selectedTab: NotificationType;
    organizationsMap: Map<string, string>;
}

const ITEMS_PER_PAGE = 10;

export const NotificationList = ({
    notifications,
    selectedTab,
    organizationsMap,
}: NotificationListProps) => {
    const [visibleCount, setVisibleCount] = React.useState(ITEMS_PER_PAGE);
    const [eventNamesById, setEventNamesById] = React.useState<
        Map<string, string>
    >(() => new Map());
    const prevNotificationLengthRef = React.useRef(notifications.length);
    const prevTabRef = React.useRef(selectedTab);

    // Only reset visible count when tab changes or notification list becomes smaller
    React.useEffect(() => {
        const tabChanged = prevTabRef.current !== selectedTab;
        const listBecameSmaller =
            notifications.length < prevNotificationLengthRef.current;

        if (tabChanged || listBecameSmaller) {
            setVisibleCount(ITEMS_PER_PAGE);
        } else if (notifications.length > prevNotificationLengthRef.current) {
            // If new notifications are added, don't reset - maintain current position
            // but ensure we don't exceed the new total
            setVisibleCount((prev) => Math.min(prev, notifications.length));
        }

        prevNotificationLengthRef.current = notifications.length;
        prevTabRef.current = selectedTab;
    }, [notifications.length, selectedTab]);

    React.useEffect(() => {
        const orgIds = Array.from(
            new Set(
                notifications
                    .filter((n) => n.type === "event")
                    .map((n) => n.orgId)
            )
        );

        if (orgIds.length === 0) {
            setEventNamesById(new Map());
            return;
        }

        let cancelled = false;

        void (async () => {
            const responses = await Promise.all(
                orgIds.map((orgId) =>
                    getEventsByOrganizationIdCached(orgId)
                )
            );
            if (cancelled) {
                return;
            }
            const next = new Map<string, string>();
            for (const res of responses) {
                if (isHttpSuccess(res.status) && Array.isArray(res.data)) {
                    for (const ev of res.data) {
                        next.set(ev.uuid, ev.name);
                    }
                }
            }
            setEventNamesById(next);
        })();

        return () => {
            cancelled = true;
        };
    }, [notifications]);

    if (notifications.length === 0) {
        return (
            <Box
                sx={{
                    padding: "20px",
                    textAlign: "center",
                }}
            >
                <Typography
                    sx={{
                        color: "#828fab",
                        fontSize: "14px",
                    }}
                >
                    No {selectedTab} notifications
                </Typography>
            </Box>
        );
    }

    const visibleNotifications = notifications.slice(0, visibleCount);
    const hasMore = visibleCount < notifications.length;

    const loadMore = () => {
        setVisibleCount((prev) =>
            Math.min(prev + ITEMS_PER_PAGE, notifications.length)
        );
    };

    return (
        <div
            id="scrollableNotificationList"
            style={{ maxHeight: "600px", overflowY: "auto" }}
        >
            <InfiniteScroll
                dataLength={visibleNotifications.length}
                next={loadMore}
                hasMore={hasMore}
                loader={
                    <Box sx={{ padding: "20px", textAlign: "center" }}>
                        <Typography
                            sx={{
                                color: "#828fab",
                                fontSize: "14px",
                            }}
                        >
                            Loading more notifications...
                        </Typography>
                    </Box>
                }
                scrollableTarget="scrollableNotificationList"
                endMessage={
                    notifications.length > ITEMS_PER_PAGE ? (
                        <Box sx={{ padding: "20px", textAlign: "center" }}>
                            <Typography
                                sx={{
                                    color: "#828fab",
                                    fontSize: "12px",
                                }}
                            >
                                All notifications loaded
                            </Typography>
                        </Box>
                    ) : null
                }
            >
                <List sx={{ padding: 0 }}>
                    {visibleNotifications.map((notification, index) => (
                        <React.Fragment key={notification.id}>
                            {notification.type === "sensor" && (
                                <SensorAlert
                                    notification={notification}
                                    organizationsMap={organizationsMap}
                                />
                            )}
                            {notification.type === "download" && (
                                <DataDownloadAlert
                                    notification={notification}
                                    organizationsMap={organizationsMap}
                                />
                            )}
                            {notification.type === "event" && (
                                <EventAlert
                                    notification={notification}
                                    organizationsMap={organizationsMap}
                                    eventNamesById={eventNamesById}
                                />
                            )}
                            {index < visibleNotifications.length - 1 && (
                                <Divider
                                    sx={{
                                        backgroundColor: "#3D475E",
                                    }}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </List>
            </InfiniteScroll>
        </div>
    );
};
